import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Area, LanguageEnum, Currency, BB_MENU_IDS, BUSINESS_BOOKER_USER_ROLES, DATE_TYPE, CountryCode, ShortCountry } from '@whitbread-eos/api';
import { format, parse, parseISO, subYears } from 'date-fns';
import { de, enGB } from 'date-fns/locale';
import parseHtml from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import getConfig from 'next/config';
import { isStringValid, logicalOrOperator } from '../validators';
import { getPathForLocale } from './getPathForLocale';
export function getObjectFormatNumber(fullNumber, language) {
  switch (language) {
    case CountryCode.EN:
      return {
        prefix: "+44",
        phoneNumber: fullNumber,
        countryCode: ShortCountry.GB
      };
    case CountryCode.DE:
      return {
        prefix: "+49",
        phoneNumber: fullNumber,
        countryCode: ShortCountry.DE
      };
  }
}
export function parsePhoneNumber(fullNumber, language) {
  if (!fullNumber.includes('+')) {
    return getObjectFormatNumber(fullNumber, language);
  }
  var phoneDetails = parsePhoneNumberFromString(fullNumber);
  if (!phoneDetails) {
    return getObjectFormatNumber('', language);
  } else {
    return {
      prefix: "+".concat(phoneDetails === null || phoneDetails === void 0 ? void 0 : phoneDetails.countryCallingCode),
      phoneNumber: phoneDetails === null || phoneDetails === void 0 ? void 0 : phoneDetails.nationalNumber,
      countryCode: phoneDetails === null || phoneDetails === void 0 ? void 0 : phoneDetails.country
    };
  }
}
export function formatCurrency(currencyCode) {
  switch (currencyCode) {
    case 'GBP':
      return Currency.GBP;
    case 'EUR':
      return Currency.EUR;
    default:
      return '';
  }
}
export function formatPrice(currency, price) {
  var language = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : LanguageEnum.ENGLISH;
  if (language === LanguageEnum.GERMAN && currency === Currency.EUR) {
    return "".concat(price === null || price === void 0 ? void 0 : price.toString().replace('.', ',')).concat(currency);
  }
  return "".concat(currency).concat(price);
}
export function formatPriceWithDecimal(language, currency, price, decimals) {
  var priceWithDecimal = price % 1 === 0 ? price : price.toFixed(2);
  if (decimals) priceWithDecimal = price.toFixed(2);
  if (currency === Currency.GBP || language === LanguageEnum.ENGLISH) {
    return "".concat(currency).concat(priceWithDecimal);
  }
  var updatedPrice = priceWithDecimal.toString().replace('.', ',');
  return "".concat(updatedPrice, " ").concat(currency);
}
export function formatRatePrice(price) {
  return price - Math.floor(price) !== 0 ? price === null || price === void 0 ? void 0 : price.toFixed(2) : price;
}
export function getPriceValueWithDecimal(price) {
  var _price$toString;
  var decimal = price === null || price === void 0 || (_price$toString = price.toString) === null || _price$toString === void 0 ? void 0 : _price$toString.call(price).replace(/[^0-9.]+/g, '');
  if (!price || isNaN(Number(decimal)) || !decimal) {
    return 0.0;
  }
  return Number(Number(decimal).toFixed(1));
}
export function formatDataTestId() {
  var prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var dataTestId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var previousString = isStringValid(prefix) ? "".concat(prefix, "-") : '';
  var testId = isStringValid(dataTestId) ? dataTestId : '';
  return "".concat(previousString).concat(testId);
}
export function formatAssetsUrl(path) {
  var useBasicAuth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var _ref = getConfig() || {},
    _ref$publicRuntimeCon = _ref.publicRuntimeConfig,
    publicRuntimeConfig = _ref$publicRuntimeCon === void 0 ? {} : _ref$publicRuntimeCon;
  if (!path) return '';
  if (useBasicAuth) {
    return "".concat(publicRuntimeConfig.NEXT_PUBLIC_ASSETS_URL).concat(path === null || path === void 0 ? void 0 : path.replace(publicRuntimeConfig.NEXT_PUBLIC_ASSETS_URL, ''));
  }
  return "".concat(publicRuntimeConfig.NEXT_PUBLIC_ASSETS_URL_WITHOUT_BASIC).concat(path === null || path === void 0 ? void 0 : path.replace(publicRuntimeConfig.NEXT_PUBLIC_ASSETS_URL_WITHOUT_BASIC, ''));
}
export function formatInnerHTMLAssetUrls(innerHTML) {
  if (!innerHTML) {
    return '';
  }
  if (!innerHTML.includes('href="/')) {
    return innerHTML;
  }
  var hrefAttributePattern = /href="\/[^"]+?\.(?!html\b)\w+?"/g;
  return innerHTML.replace(hrefAttributePattern, hrefAttribute => "href=\"".concat(formatAssetsUrl(hrefAttribute.substring(6, hrefAttribute.length - 1)), "\""));
}
export function formatDate(date, formatDate, language) {
  var isoDate = date.includes('-') ? parseISO(date) : parseISO(format(new Date(date), DATE_TYPE.YEAR_MONTH_DAY));
  return format(isoDate, formatDate, {
    locale: language === LanguageEnum.GERMAN ? de : enGB
  });
}
export function checkDateFormatter(time, date, language) {
  if (language === LanguageEnum.ENGLISH) {
    return format(parseISO("".concat(date, "T").concat(time)), 'ha - EEE d LLL yyyy', {
      locale: enGB
    }).replace('AM', 'am').replace('PM', 'pm');
  }
  return format(parseISO("".concat(date, "T").concat(time)), 'H:mm - EEE d LLL yyyy', {
    locale: de
  });
}
export function formatNextLocaleLink(pathName, nextLocale, currentLocale, area) {
  var _pathName, _pathName2, _pathName3, _linkWithNextLocale$r;
  var homepage = "".concat(nextLocale === LanguageEnum.ENGLISH ? '/gb/en/' : '/de/de/').concat(area === Area.PI ? 'home.html' : '').concat(area === Area.BB ? 'business-booker/home.html' : '');
  var nextLocaleLink = nextLocale === LanguageEnum.ENGLISH ? '/gb' : '/de';
  pathName = nextLocale === LanguageEnum.GERMAN ? (_pathName = pathName) === null || _pathName === void 0 ? void 0 : _pathName.replace('germany', 'deutschland') : (_pathName2 = pathName) === null || _pathName2 === void 0 ? void 0 : _pathName2.replace('deutschland', 'germany');
  var linkWithNextLocale = !((_pathName3 = pathName) !== null && _pathName3 !== void 0 && _pathName3.length) ? '/gb/en' : "".concat(nextLocaleLink).concat(pathName);
  if (currentLocale === nextLocale) {
    return linkWithNextLocale;
  }
  var pathPreservePages = new RegExp('home|search|pre-check-in');
  if (!pathName || !pathPreservePages.test(pathName)) {
    return homepage;
  }
  var linkWithNextCountry = linkWithNextLocale === null || linkWithNextLocale === void 0 || (_linkWithNextLocale$r = linkWithNextLocale.replace('/gb/de', '/gb/en')) === null || _linkWithNextLocale$r === void 0 ? void 0 : _linkWithNextLocale$r.replace('/de/en', '/de/de');
  return linkWithNextCountry;
}
export function formatFindBookingToken(token) {
  if (isStringValid(token)) {
    return encodeURIComponent(token);
  }
  return token;
}
export function formatUrlTermsConditions(text) {
  if (!text) return '';
  var htmlSuffix = '.html';
  var htmlIndex = text.indexOf(htmlSuffix);
  if (htmlIndex === -1) return '';
  var slashIndex = text.indexOf('/');
  if (slashIndex === -1 || slashIndex > htmlIndex) return '';
  var path = text.slice(slashIndex, htmlIndex + htmlSuffix.length);
  var formattedPath = formatAssetsUrl(path);
  return text.slice(0, slashIndex) + formattedPath + text.slice(htmlIndex + htmlSuffix.length);
}
export function formatGuestTitleOptions() {
  var title = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var words = String(title).replace(/'/g, '').split(',');
  return words.map(option => {
    return {
      id: option,
      label: option
    };
  });
}
export function formatTextWithSpace(text) {
  return text.split(' ').join('-');
}
export function replaceWithEmptyString(receivedString) {
  var _receivedString$repla;
  return (_receivedString$repla = receivedString === null || receivedString === void 0 ? void 0 : receivedString.replace(/[^\w]/g, '')) !== null && _receivedString$repla !== void 0 ? _receivedString$repla : '';
}
export function uppercaseAndReplace(val, regex, replaceStr) {
  return val.toUpperCase().replace(regex, replaceStr);
}
export function upperFirst(value) {
  return "".concat(value.charAt(0).toUpperCase()).concat(value.slice(1));
}
export function upperOnlyFirst(value) {
  return "".concat(value.charAt(0).toUpperCase()).concat(value.slice(1).toLowerCase());
}
export function swapKeysAndValues(obj) {
  var swapped = Object.entries(obj).map(_ref2 => {
    var _ref3 = _slicedToArray(_ref2, 2),
      key = _ref3[0],
      value = _ref3[1];
    return [value, key];
  });
  return Object.fromEntries(swapped);
}
export function formatForMobile(list) {
  var firstLabels = [];
  var lastLabels = [];
  list.forEach(label => {
    if (label.id === BB_MENU_IDS.COMPANY) {
      var _label$subNav;
      firstLabels.push({
        navTitle: label.navTitle,
        subNav: [],
        id: BB_MENU_IDS.COMPANY_NAME
      });
      (_label$subNav = label.subNav) === null || _label$subNav === void 0 || (_label$subNav = _label$subNav[0].navOptions) === null || _label$subNav === void 0 || _label$subNav.forEach(subLabel => {
        firstLabels.push(_objectSpread({
          navTitle: subLabel.title,
          subNav: [{
            navOptions: subLabel.subMenuLinks || [],
            title: subLabel.title
          }],
          id: BB_MENU_IDS.COMPANY
        }, subLabel.url && {
          url: subLabel.url
        }));
      });
    } else {
      lastLabels.push(label);
    }
  });
  return [...firstLabels, ...lastLabels];
}
export function superRoleFilter(role, list) {
  if (role !== BUSINESS_BOOKER_USER_ROLES.SUPER) {
    return list === null || list === void 0 ? void 0 : list.map(item => {
      var _item$navOptions;
      var filtered = item === null || item === void 0 || (_item$navOptions = item.navOptions) === null || _item$navOptions === void 0 ? void 0 : _item$navOptions.filter(label => {
        return !label.superRole;
      });
      return _objectSpread(_objectSpread({}, item), {}, {
        navOptions: filtered
      });
    });
  }
  return list;
}
export function langFilter(language, list) {
  if (language === 'de') {
    return list === null || list === void 0 ? void 0 : list.filter(item => item.id !== BB_MENU_IDS.BUSINESS);
  }
  return list;
}
export function removeHtmlTags(inputString) {
  if (!inputString) return '';
  return DOMPurify.sanitize(inputString, {
    ALLOWED_TAGS: []
  });
}
export function filterAncillaryCloseOutData(data) {
  var _data$items;
  if (!(data !== null && data !== void 0 && data.items)) {
    return [];
  }
  return data === null || data === void 0 || (_data$items = data.items) === null || _data$items === void 0 ? void 0 : _data$items.filter(item => {
    if (item !== null && item !== void 0 && item.startDate && item !== null && item !== void 0 && item.endDate) {
      return parse(item.endDate, 'dd/MM/yyyy', new Date()) > parse(item.startDate, 'dd/MM/yyyy', new Date());
    }
  });
}
export function createReservationDetails(receivedArrivalDate, receivedDepartureDate, currencyCode, reservationByIdList, noNights) {
  var arrivalDate = logicalOrOperator(receivedArrivalDate, null);
  var departureDate = logicalOrOperator(receivedDepartureDate, null);
  var currency = currencyCode;
  var noRooms = reservationByIdList === null || reservationByIdList === void 0 ? void 0 : reservationByIdList.length;
  return {
    arrivalDate,
    departureDate,
    currency,
    noRooms,
    noNights
  };
}
export function matchSilentSubstitutions(reservations, substitutions) {
  var matchedSubstitutions = [];
  var savedSubstitutions = [...substitutions];
  for (var room of reservations) {
    for (var i = 0; i < savedSubstitutions.length; i++) {
      var _savedSubstitutions$i, _room$roomStay, _savedSubstitutions$i2, _room$roomStay2, _savedSubstitutions$i3, _room$roomStay3, _savedSubstitutions$i4;
      var matched = ((_savedSubstitutions$i = savedSubstitutions[i]) === null || _savedSubstitutions$i === void 0 ? void 0 : _savedSubstitutions$i.filtered) !== true && (room === null || room === void 0 || (_room$roomStay = room.roomStay) === null || _room$roomStay === void 0 ? void 0 : _room$roomStay.adultsNumber) === ((_savedSubstitutions$i2 = savedSubstitutions[i]) === null || _savedSubstitutions$i2 === void 0 ? void 0 : _savedSubstitutions$i2.adults) && (room === null || room === void 0 || (_room$roomStay2 = room.roomStay) === null || _room$roomStay2 === void 0 ? void 0 : _room$roomStay2.childrenNumber) === ((_savedSubstitutions$i3 = savedSubstitutions[i]) === null || _savedSubstitutions$i3 === void 0 ? void 0 : _savedSubstitutions$i3.children) && (room === null || room === void 0 || (_room$roomStay3 = room.roomStay) === null || _room$roomStay3 === void 0 || (_room$roomStay3 = _room$roomStay3.roomExtraInfo) === null || _room$roomStay3 === void 0 ? void 0 : _room$roomStay3.roomType) === ((_savedSubstitutions$i4 = savedSubstitutions[i]) === null || _savedSubstitutions$i4 === void 0 ? void 0 : _savedSubstitutions$i4.pmsRoomType);
      if (matched) {
        var filteredSubstitution = _objectSpread(_objectSpread({}, savedSubstitutions[i]), {}, {
          filtered: true
        });
        matchedSubstitutions.push(filteredSubstitution);
        savedSubstitutions[i] = filteredSubstitution;
        break;
      }
    }
  }
  return matchedSubstitutions;
}
export var getYearsAgoFromNow = year => subYears(new Date(), year);
export function transformLabels(staticData) {
  var _staticData$labels, _staticData$labels2, _staticData$labels3, _staticData$labels4, _staticData$labels5, _staticData$labels6, _staticData$labels7;
  var main = staticData !== null && staticData !== void 0 && (_staticData$labels = staticData.labels) !== null && _staticData$labels !== void 0 && _staticData$labels.main ? JSON.parse(staticData.labels.main) : {};
  var piBookings = staticData !== null && staticData !== void 0 && (_staticData$labels2 = staticData.labels) !== null && _staticData$labels2 !== void 0 && _staticData$labels2.piBookings ? JSON.parse(staticData.labels.piBookings) : {};
  var booking = staticData !== null && staticData !== void 0 && (_staticData$labels3 = staticData.labels) !== null && _staticData$labels3 !== void 0 && _staticData$labels3.booking ? JSON.parse(staticData.labels.booking) : {};
  var piPreCheckIn = staticData !== null && staticData !== void 0 && (_staticData$labels4 = staticData.labels) !== null && _staticData$labels4 !== void 0 && _staticData$labels4.piPreCheckIn ? JSON.parse(staticData.labels.piPreCheckIn) : {};
  var piGroupBooking = staticData !== null && staticData !== void 0 && (_staticData$labels5 = staticData.labels) !== null && _staticData$labels5 !== void 0 && _staticData$labels5.piGroupBooking ? JSON.parse(staticData.labels.piGroupBooking) : {};
  var extras = staticData !== null && staticData !== void 0 && (_staticData$labels6 = staticData.labels) !== null && _staticData$labels6 !== void 0 && _staticData$labels6.extras ? JSON.parse(staticData.labels.extras) : {};
  var promotions = staticData !== null && staticData !== void 0 && (_staticData$labels7 = staticData.labels) !== null && _staticData$labels7 !== void 0 && _staticData$labels7.promotions ? JSON.parse(staticData.labels.promotions) : {};
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, main), piBookings), booking), piPreCheckIn), piGroupBooking), extras), promotions);
}
export function renderSanitizedHtml(html) {
  var sanitizedHtml = DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', 'style']
  });
  var formattedHtml = formatInnerHTMLAssetUrls(sanitizedHtml);
  return parseHtml(formattedHtml);
}
export function formatBillingAddress(address, companyName) {
  var _ref4, _address$line, _ref5, _address$line2, _ref6, _address$line3, _ref7, _address$line4, _ref8, _address$line5, _ref9, _address$postCode, _ref0, _address$addressType, _ref1, _address$countryCode;
  if (!address) {
    return null;
  }
  var addressObj = {
    addressLine1: (_ref4 = (_address$line = address === null || address === void 0 ? void 0 : address.line1) !== null && _address$line !== void 0 ? _address$line : address === null || address === void 0 ? void 0 : address.addressLine1) !== null && _ref4 !== void 0 ? _ref4 : '',
    addressLine2: (_ref5 = (_address$line2 = address === null || address === void 0 ? void 0 : address.line2) !== null && _address$line2 !== void 0 ? _address$line2 : address === null || address === void 0 ? void 0 : address.addressLine2) !== null && _ref5 !== void 0 ? _ref5 : '',
    addressLine3: (_ref6 = (_address$line3 = address === null || address === void 0 ? void 0 : address.line3) !== null && _address$line3 !== void 0 ? _address$line3 : address === null || address === void 0 ? void 0 : address.addressLine3) !== null && _ref6 !== void 0 ? _ref6 : '',
    addressLine4: (_ref7 = (_address$line4 = address === null || address === void 0 ? void 0 : address.line4) !== null && _address$line4 !== void 0 ? _address$line4 : address === null || address === void 0 ? void 0 : address.addressLine4) !== null && _ref7 !== void 0 ? _ref7 : '',
    cityName: (_ref8 = (_address$line5 = address === null || address === void 0 ? void 0 : address.line4) !== null && _address$line5 !== void 0 ? _address$line5 : address === null || address === void 0 ? void 0 : address.addressLine4) !== null && _ref8 !== void 0 ? _ref8 : '',
    postalCode: (_ref9 = (_address$postCode = address === null || address === void 0 ? void 0 : address.postCode) !== null && _address$postCode !== void 0 ? _address$postCode : address === null || address === void 0 ? void 0 : address.postalCode) !== null && _ref9 !== void 0 ? _ref9 : '',
    companyName: companyName !== null && companyName !== void 0 ? companyName : '',
    addressType: (_ref0 = (_address$addressType = address === null || address === void 0 ? void 0 : address.addressType) !== null && _address$addressType !== void 0 ? _address$addressType : address === null || address === void 0 ? void 0 : address.type) !== null && _ref0 !== void 0 ? _ref0 : 'BUSINESS'
  };
  return _objectSpread(_objectSpread({}, addressObj), {}, {
    country: (_ref1 = (_address$countryCode = address === null || address === void 0 ? void 0 : address.countryCode) !== null && _address$countryCode !== void 0 ? _address$countryCode : address === null || address === void 0 ? void 0 : address.country) !== null && _ref1 !== void 0 ? _ref1 : ''
  });
}
export function formatRequiredString(value) {
  var _value$replace;
  return (_value$replace = value === null || value === void 0 ? void 0 : value.replace(/\*/g, '')) !== null && _value$replace !== void 0 ? _value$replace : '';
}
function formatGuestAddress(data) {
  var isSomeoneElseAndSingleRoomRedesign = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var leadGuest = data.bookingForSomeoneElse || isSomeoneElseAndSingleRoomRedesign ? data.leadGuest[0] : data;
  return {
    cityName: leadGuest === null || leadGuest === void 0 ? void 0 : leadGuest.addressLine4,
    countryCode: leadGuest === null || leadGuest === void 0 ? void 0 : leadGuest.countryCode,
    addressLine1: leadGuest === null || leadGuest === void 0 ? void 0 : leadGuest.addressLine1,
    addressLine2: leadGuest === null || leadGuest === void 0 ? void 0 : leadGuest.addressLine2,
    addressLine3: leadGuest === null || leadGuest === void 0 ? void 0 : leadGuest.addressLine3,
    addressLine4: leadGuest === null || leadGuest === void 0 ? void 0 : leadGuest.addressLine4,
    postalCode: leadGuest === null || leadGuest === void 0 ? void 0 : leadGuest.postcodeAddress
  };
}
export function formatAccompanyingGuest(guest, data) {
  var _data$accompanyingfir = data.accompanyingfirstName,
    accompanyingfirstName = _data$accompanyingfir === void 0 ? undefined : _data$accompanyingfir,
    _data$accompanyinglas = data.accompanyinglastName,
    accompanyinglastName = _data$accompanyinglas === void 0 ? undefined : _data$accompanyinglas,
    _data$accompanyingtit = data.accompanyingtitle,
    accompanyingtitle = _data$accompanyingtit === void 0 ? undefined : _data$accompanyingtit;
  return _objectSpread(_objectSpread({}, guest), {}, {
    accompanyingGuestDetails: {
      title: accompanyingtitle,
      firstName: accompanyingfirstName,
      lastName: accompanyinglastName
    }
  });
}
function formatLeadGuest(data, item) {
  var isAccompanyingGuestEnabled = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var guest = {
    sameAsBooker: !!item.stayInThisRoom,
    stayingGuestDetails: {
      title: item.stayInThisRoom ? data.title : item.title,
      firstName: item.stayInThisRoom ? data.firstName : item.firstName,
      lastName: item.stayInThisRoom ? data.lastName : item.lastName,
      address: {
        cityName: item.stayInThisRoom ? data.addressLine4 : item.addressLine4,
        countryCode: item.stayInThisRoom ? data.countryCode : item.countryCode,
        addressLine1: item.stayInThisRoom ? data.addressLine1 : item.addressLine1,
        addressLine2: item.stayInThisRoom ? data.addressLine2 : item.addressLine2,
        addressLine3: item.stayInThisRoom ? data.addressLine3 : item.addressLine3,
        addressLine4: item.stayInThisRoom ? data.addressLine4 : item.addressLine4,
        postalCode: item.stayInThisRoom ? data.postcodeAddress : item.postcodeAddress
      }
    }
  };
  if (isAccompanyingGuestEnabled && item) {
    guest = formatAccompanyingGuest(guest, item);
  }
  return guest;
}
export function formatGuests(data) {
  var isSomeoneElseAndSingleRoomRedesign = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var isAccompanyingGuestEnabled = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var stayingGuests = [];
  if (data.leadGuest && data.leadGuest.length > 1) {
    data.leadGuest.map(item => {
      var guest = formatLeadGuest(data, item, isAccompanyingGuestEnabled);
      stayingGuests.push(guest);
    });
  } else {
    var _data$leadGuest;
    var guest = {
      sameAsBooker: data.bookingForSomeoneElse || isSomeoneElseAndSingleRoomRedesign ? false : true,
      stayingGuestDetails: {
        title: data.bookingForSomeoneElse || isSomeoneElseAndSingleRoomRedesign ? data.leadGuest[0].title : data.title,
        firstName: data.bookingForSomeoneElse || isSomeoneElseAndSingleRoomRedesign ? data.leadGuest[0].firstName : data.firstName,
        lastName: data.bookingForSomeoneElse || isSomeoneElseAndSingleRoomRedesign ? data.leadGuest[0].lastName : data.lastName,
        address: formatGuestAddress(data, isSomeoneElseAndSingleRoomRedesign)
      }
    };
    if (isAccompanyingGuestEnabled && data !== null && data !== void 0 && (_data$leadGuest = data.leadGuest) !== null && _data$leadGuest !== void 0 && _data$leadGuest[0]) {
      guest = formatAccompanyingGuest(guest, data.leadGuest[0]);
    }
    stayingGuests.push(guest);
  }
  return stayingGuests;
}
export { getPathForLocale };